import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import SEOSchema from '@/components/feature/SEOSchema';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 无码开发平台 | 低代码无码${currentCategory.name}解决方案`;
    }
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-background-50">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOSchema
        pageType="category"
        pageTitle={`${currentCategory.name} - 无码开发平台`}
        pageDescription={currentCategory.description}
        canonicalPath={`/category/${currentCategory.slug}`}
        breadcrumbs={[
          { name: '首页', path: '/' },
          { name: currentCategory.name, path: `/category/${currentCategory.slug}` },
        ]}
      />
      <main className="min-h-screen bg-background-50">
        <section className="pt-28 pb-12 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
            </nav>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-50 text-accent-600">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">{currentCategory.name}</h1>
                <p className="text-foreground-600 text-sm mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-10 pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-4 -mt-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group p-6 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-xs text-foreground-500">{article.date}</span>
                      <span className="text-xs text-foreground-500">&middot;</span>
                      <span className="text-xs text-foreground-500">{article.readTime}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3 mb-4">{article.summary}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">{tag}</span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}